import React, { useState, useEffect, useRef } from 'react';
import { useToast } from '../hooks/use-toast';

const API = `/api`;

// Floating-label field (Adyen-style). Uses CSS classes from index.css.
// Uses a ref to bind native `invalid` event so we can override the default
// browser validation message (which ends with a period) reliably.
const Field = ({ id, label, type = 'text', required, value, onChange, testId, textarea, tooltip, maxLength }) => {
  const elRef = useRef(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const onInvalid = (e) => {
      const v = e.target.validity;
      if (v.valueMissing) {
        e.target.setCustomValidity('Please fill in this field');
      } else if (v.typeMismatch) {
        e.target.setCustomValidity('Please enter a valid value');
      } else {
        e.target.setCustomValidity('');
      }
    };
    el.addEventListener('invalid', onInvalid);
    return () => el.removeEventListener('invalid', onInvalid);
  }, []);

  const handleInput = (e) => {
    e.target.setCustomValidity('');
    onChange(e);
  };

  const sharedProps = {
    id,
    name: id,
    required,
    value,
    onChange: handleInput,
    placeholder: ' ',
    title: tooltip || undefined,
    'data-testid': testId,
    ref: elRef,
    ...(maxLength ? { maxLength } : {}),
  };

  return (
    <div className="float-field" title={tooltip || undefined}>
      {textarea ? <textarea {...sharedProps} /> : <input type={type} {...sharedProps} />}
      <label htmlFor={id}>
        {label}{required && <span className="text-[#ef5b25]"> *</span>}
      </label>
    </div>
  );
};

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    businessName: '',
    businessLocation: '',
    enquiry: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

 const onSubmit = async (e) => {
   console.log("Contact form submitted");
   alert("Contact form submitted");
  e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      console.log("Calling API:", `${API}/contact`);
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.fullName,
          email: form.email,
          business_name: form.businessName,
          business_type_location: form.businessLocation,
          message: form.enquiry,
        }),
      });
      if (!res.ok) throw new Error('Failed');
      toast({
        title: 'Message sent',
        description: "Thanks — we'll be in touch within one business day.",
      });
      setForm({ fullName: '', email: '', businessName: '', businessLocation: '', enquiry: '' });
    } catch {
      toast({
        title: 'Could not send message',
        description: 'Please try again in a moment or email sales@ecomm365.com.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-20 px-4 sm:px-6 lg:px-8 bg-[#f7f9fc] overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 items-center">
          {/* Image — left side */}
          <div className="lg:col-span-7 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5">
              <img
                src="https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/j1x9mltm_IMAGE%201.jpg"
                alt="Friendly support — Have a question? We're here to help."
                className="w-full h-[380px] sm:h-[440px] lg:h-[500px] object-cover"
                data-testid="contact-portrait"
              />
              <div
                aria-hidden="true"
                className="absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-black/15 to-transparent pointer-events-none hidden lg:block"
              />
            </div>
          </div>

          {/* Form — right side, overlaps the image on desktop */}
          <div className="lg:col-span-5 lg:-ml-20 relative z-10">
            <div className="bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 p-6 sm:p-8">
              <h2
                className="font-handwritten text-[2rem] sm:text-[2.5rem] leading-[1.05] text-[#0a1929] mb-3"
                data-testid="contact-heading"
              >
                Have a question? We're here to help.
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed">
                Speak with our team about card machines, eCommerce, EPOS, integrated
                payment solutions or general business enquiries. From product guidance
                to tailored recommendations, we're here to help you find the right fit
                for your business.
              </p>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="contact-form" noValidate={false}>
                <Field
                  id="fullName"
                  label="Full name"
                  required
                  value={form.fullName}
                  onChange={onChange}
                  tooltip="Please enter your first name and surname"
                  testId="contact-fullname-input"
                />
                <Field
                  id="email"
                  label="Business Email"
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange}
                  tooltip="Please enter a valid email address"
                  testId="contact-email-input"
                />
                <Field
                  id="businessName"
                  label="Business Name"
                  value={form.businessName}
                  onChange={onChange}
                  tooltip="Please enter the name of your business"
                  testId="contact-business-name-input"
                />
                <Field
                  id="businessLocation"
                  label="Business Location"
                  required
                  value={form.businessLocation}
                  onChange={onChange}
                  tooltip="Please enter the country where your business is located"
                  testId="contact-business-location-input"
                />
                <Field
                  id="enquiry"
                  label="Tell us about your enquiry"
                  required
                  textarea
                  maxLength={2000}
                  value={form.enquiry}
                  onChange={onChange}
                  tooltip="Share a few details about your enquiry"
                  testId="contact-enquiry-input"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="group w-full inline-flex items-center justify-center gap-2 bg-[#ef5b25] hover:bg-[#d44d1c] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3.5 px-6 rounded-lg transition-colors duration-200 mt-1"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? 'Sending…' : 'Contact our Team'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
